import React, { useState, useMemo } from "react";
import CustomDataTable from "../shared/CustomDataTable";
import { t } from "../../../i18n";

/**
 * Format points for display (remove decimals if whole number)
 */
const formatPoints = (points) => {
  const parsed = parseFloat(points);
  if (isNaN(parsed)) return "0";
  return parsed % 1 === 0
    ? parsed.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    : parsed.toLocaleString("pt-PT");
};

/**
 * Get initials from full name
 */
const getInitials = (fullName) => {
  if (!fullName) return "??";
  const names = fullName
    .trim()
    .split(" ")
    .filter(Boolean);
  if (names.length === 0) return "??";
  if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

/**
 * Avatar component with image or initials fallback
 */
const Avatar = ({ src, name, isCurrentUser }) => {
  const [imgError, setImgError] = useState(false);
  const initials = getInitials(name);

  const baseClass = "ranking-table__avatar";
  const currentUserClass = isCurrentUser
    ? "ranking-table__avatar--current"
    : "";

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={name}
        className={`${baseClass} ${currentUserClass}`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className={`${baseClass} ${baseClass}--initials ${currentUserClass}`}>
      {initials}
    </div>
  );
};

/**
 * Main RankingTable component using CustomDataTable
 */
const RankingTable = (props) => {
  const {
    userPosition,
    rankingLabel,
    currPage,
    totalPages,
    onPageChange,
    winners = [],
    userNeighbours = [],
  } = props;

  // Merge winners and userNeighbours (avoiding duplicates)
  const data = useMemo(() => {
    const mergedResults = [...winners];
    userNeighbours.forEach((user) => {
      if (!mergedResults.find((w) => w.id === user.id)) {
        mergedResults.push(user);
      }
    });
    return mergedResults;
  }, [winners, userNeighbours]);

  // Check if a result is the current user
  const isCurrentUser = (result) => {
    if (!userPosition) return false;
    return (
      result.id === userPosition.id || result.position === userPosition.position
    );
  };

  const pointsLabel =
    rankingLabel && rankingLabel.currencyLabel
      ? rankingLabel.currencyLabel
      : t("gamification2026.rankings.points");

  // Column definitions
  const columns = [
    {
      key: "position",
      label: t("gamification2026.rankings.position"),
      sortKey: "position",
    },
    {
      key: "player",
      label: "",
      sortable: false,
    },
    {
      key: "points",
      label: pointsLabel,
      sortKey: "points",
      align: "right",
    },
  ];

  // Sort function
  const sortFn = (data, sort) => {
    return [...data].sort((a, b) => {
      const multiplier = sort.direction === "asc" ? 1 : -1;
      if (sort.key === "position") {
        return (a.position - b.position) * multiplier;
      }
      if (sort.key === "points") {
        return (
          (parseFloat(a.total_points) - parseFloat(b.total_points)) * multiplier
        );
      }
      return 0;
    });
  };

  // Custom row renderer
  const renderRow = (result, index, className) => {
    const isCurrent = isCurrentUser(result);
    const rowClass = isCurrent
      ? `data-table__row data-table__row--current ${className}__row ${className}__row--current`
      : `data-table__row ${className}__row`;

    return (
      <tr key={result.id || index} className={rowClass}>
        <td className={`data-table__cell ${className}__cell ${className}__cell--position`}>
          {result.position}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--player`}>
          <div className={`${className}__player`}>
            <Avatar
              src={result.avatar}
              name={result.full_name}
              isCurrentUser={isCurrent}
            />
            <span className={`${className}__player-name`}>
              {result.full_name}
            </span>
          </div>
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--points`}>
          {formatPoints(result.total_points)}
        </td>
      </tr>
    );
  };

  return (
    <CustomDataTable
      className="ranking-table"
      columns={columns}
      data={data}
      renderRow={renderRow}
      rowKey={(item, index) => item.id || index}
      defaultSort={{ key: "position", direction: "asc" }}
      sortFn={sortFn}
      pagination={{
        totalPages,
        currentPage: currPage,
        onPageChange,
      }}
      emptyMessage={t("gamification2026.rankings.no-results")}
    />
  );
};

export default RankingTable;
